// Lightweight, module-level diagnostics store. Lets empty-state screens copy
// a small JSON blob (user_id, role, last query error) for support emails,
// and powers the Vet Dashboard's auth debug panel.

type LastError = {
  scope: string;
  message: string;
  code?: string;
  details?: string;
  hint?: string;
  at: string; // ISO timestamp
} | null;

let lastError: LastError = null;

export const recordQueryError = (scope: string, error: unknown) => {
  if (!error) return;
  const err = error as { message?: string; code?: string; details?: string; hint?: string };
  lastError = {
    scope,
    message: err?.message || String(error),
    code: err?.code,
    details: err?.details,
    hint: err?.hint,
    at: new Date().toISOString(),
  };
};

export const recordEmptyResult = (scope: string) => {
  // Only overwrite if we don't already have a real error for this scope.
  if (lastError?.scope === scope) return;
  lastError = {
    scope,
    message: "Query returned 0 rows",
    at: new Date().toISOString(),
  };
};

export const getLastQueryError = (): LastError => lastError;

// ---------------------------------------------------------------------------
// Auth diagnostics
// ---------------------------------------------------------------------------
//
// The Vet Dashboard auth debug panel needs to answer 3 questions when a vet
// is mysteriously signed out / bounced:
//
//   1. Which gate redirected them? (e.g. RequireMfa, VetDashboard role gate,
//      AuthCallback, MfaChallenge)
//   2. What was the last auth event Supabase fired and when? (SIGNED_IN,
//      SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED)
//   3. What was the most recent error code (PostgREST 42501 etc.)?
//
// We deliberately keep this in module-level state (not React context) so
// non-React code paths (resilientQuery, AuthContext listener) can record
// without prop-drilling.
//

export type AuthEventName =
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED"
  | "PASSWORD_RECOVERY"
  | "INITIAL_SESSION"
  | "MFA_CHALLENGE_VERIFIED";

export interface AuthEventRecord {
  event: AuthEventName | string;
  at: string;
  hasSession: boolean;
  userId?: string | null;
  expiresAt?: string | null;
}

export interface GateRedirectRecord {
  gate: string; // e.g. "VetDashboard:role-mismatch", "RequireMfa:no-user"
  to: string;
  reason?: string;
  at: string;
}

export interface SignOutLogEntry {
  at: string;
  userId: string | null;
  path: string | null;
  trigger: string; // e.g. "AuthContext.signOut", "supabase:SIGNED_OUT", "Settings:signOutOthers"
  scope?: string; // "global" | "others" | "local"
  reason?: string;
  stack?: string;
}

interface AuthState {
  lastEvent: AuthEventRecord | null;
  lastRefreshAt: string | null;
  lastSignOutAt: string | null;
  lastSignOutReason: string | null;
  lastGateRedirect: GateRedirectRecord | null;
  history: AuthEventRecord[]; // last 20
  signOutLog: SignOutLogEntry[]; // last 20
}

const SIGNOUT_LOG_KEY = "gpv:signout-log";

const loadPersistedSignOutLog = (): SignOutLogEntry[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SIGNOUT_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, 20) : [];
  } catch {
    return [];
  }
};

const authState: AuthState = {
  lastEvent: null,
  lastRefreshAt: null,
  lastSignOutAt: null,
  lastSignOutReason: null,
  lastGateRedirect: null,
  history: [],
  signOutLog: loadPersistedSignOutLog(),
};

const HISTORY_LIMIT = 20;
const SIGNOUT_LOG_LIMIT = 20;

const persistSignOutLog = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIGNOUT_LOG_KEY, JSON.stringify(authState.signOutLog));
  } catch {
    /* ignore quota / privacy mode */
  }
};

export const logSignOut = (entry: Omit<SignOutLogEntry, "at" | "path"> & { path?: string | null }) => {
  const rec: SignOutLogEntry = {
    at: new Date().toISOString(),
    userId: entry.userId ?? null,
    path: entry.path ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : null),
    trigger: entry.trigger,
    scope: entry.scope,
    reason: entry.reason,
    stack: entry.stack,
  };
  authState.signOutLog = [rec, ...authState.signOutLog].slice(0, SIGNOUT_LOG_LIMIT);
  authState.lastSignOutAt = rec.at;
  authState.lastSignOutReason = rec.reason || rec.trigger;
  persistSignOutLog();
  // Mirror to console so it shows up in production session-replay/console captures.
  // eslint-disable-next-line no-console
  console.warn("[auth:signout]", rec);
};

export const getSignOutLog = (): ReadonlyArray<SignOutLogEntry> => authState.signOutLog;

export const clearSignOutLog = () => {
  authState.signOutLog = [];
  persistSignOutLog();
};

// ---------------------------------------------------------------------------
// Vet availability ("offline reason") log - going offline for GetVideoVet does
// NOT sign the user out, it just flips vet_availability_now.is_available to
// false. Surfaced in the Vet Auth Debug panel + the AvailableNowToggle card.
// ---------------------------------------------------------------------------

export interface OfflineSnapshot {
  visibility_state?: string;
  page_lifecycle_state?: string;
  has_focus?: boolean;
  online?: boolean;
  url?: string;
  viewport?: { w: number; h: number };
  screen?: { w: number; h: number };
  device_pixel_ratio?: number;
  wake_lock_supported?: boolean;
  user_agent?: string;
  // ----- Coarse lock indicators (added) -----
  /** ms since last user input (pointer/key/touch). Long gaps suggest the device is locked or unattended. */
  ms_since_last_interaction?: number;
  last_interaction_at?: string | null;
  /** Whether a screen wake lock is currently held by this tab. */
  wake_lock_active?: boolean;
  wake_lock_last_change_at?: string | null;
  wake_lock_last_change?: "acquired" | "released" | null;
  /** Best-effort Page Lifecycle state. */
  doc_state?: string;
  /** Most recent visibility transition we observed. */
  last_visibility_transition?: string | null;
  last_visibility_transition_at?: string | null;
  /** Battery charging state - discharging laptop that just slept is a strong lock signal. */
  battery_charging?: boolean | null;
  /** prefers-reduced-motion sometimes flips when the OS enters power-save / lock. */
  prefers_reduced_motion?: boolean;
  /**
   * Mirror of OfflineReasonEntry.eventId. Embedded so the backend audit row
   * carries the same correlation id without needing a dedicated column.
   */
  client_event_id?: string;
}

// Last-interaction tracker + ring buffer (used by debug-panel activity chart).
let lastInteractionAt: number | null = null;

export type InteractionKind = "pointer" | "keyboard";
export interface InteractionSample {
  at: number; // epoch ms
  kind: InteractionKind;
}
const INTERACTION_LOG_LIMIT = 600;
let interactionLog: InteractionSample[] = [];

// Coalesce high-frequency pointermove/wheel/scroll into ~1 sample per 250ms
// per kind so we don't flood the buffer.
const lastSampleAtByKind: Record<InteractionKind, number> = { pointer: 0, keyboard: 0 };
const SAMPLE_MIN_GAP_MS = 250;

const recordInteractionSample = (kind: InteractionKind) => {
  const now = Date.now();
  lastInteractionAt = now;
  if (now - lastSampleAtByKind[kind] < SAMPLE_MIN_GAP_MS) return;
  lastSampleAtByKind[kind] = now;
  interactionLog.push({ at: now, kind });
  if (interactionLog.length > INTERACTION_LOG_LIMIT) {
    interactionLog = interactionLog.slice(-INTERACTION_LOG_LIMIT);
  }
};

export const getInteractionLog = (): ReadonlyArray<InteractionSample> => interactionLog;

let interactionListenersInstalled = false;
export const installInteractionTracker = () => {
  if (interactionListenersInstalled || typeof window === "undefined") return;
  interactionListenersInstalled = true;
  const opts: AddEventListenerOptions = { passive: true, capture: true };
  ["pointerdown", "pointermove", "touchstart", "wheel", "scroll"].forEach((evt) =>
    window.addEventListener(evt, () => recordInteractionSample("pointer"), opts),
  );
  window.addEventListener("keydown", () => recordInteractionSample("keyboard"), opts);
};

// Wake-lock availability tracker - call from AvailableNowToggle whenever it
// requests/releases a screen wake lock.
let wakeLockActive = false;
let wakeLockLastChangeAt: string | null = null;
let wakeLockLastChange: "acquired" | "released" | null = null;
export const recordWakeLockChange = (change: "acquired" | "released") => {
  wakeLockActive = change === "acquired";
  wakeLockLastChange = change;
  wakeLockLastChangeAt = new Date().toISOString();
};

// Battery cache (best-effort, non-blocking).
let cachedBatteryCharging: boolean | null = null;
if (typeof navigator !== "undefined") {
  // @ts-expect-error - getBattery is non-standard
  const getBattery = navigator.getBattery;
  if (typeof getBattery === "function") {
    getBattery
      .call(navigator)
      .then((b: { charging: boolean; addEventListener: (e: string, fn: () => void) => void }) => {
        cachedBatteryCharging = b.charging;
        b.addEventListener("chargingchange", () => {
          cachedBatteryCharging = b.charging;
        });
      })
      .catch(() => {});
  }
}

export const captureOfflineSnapshot = (): OfflineSnapshot => {
  if (typeof window === "undefined") return {};
  const doc = typeof document !== "undefined" ? document : null;
  const lifecycle: string | undefined = (doc as any)?.wasDiscarded ? "discarded" : (doc as any)?.visibilityState;

  const reducedMotion =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : undefined;
  return {
    visibility_state: doc?.visibilityState,
    page_lifecycle_state: lifecycle,
    has_focus: typeof doc?.hasFocus === "function" ? doc.hasFocus() : undefined,
    online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    url: window.location?.href,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    screen: typeof window.screen !== "undefined" ? { w: window.screen.width, h: window.screen.height } : undefined,
    device_pixel_ratio: window.devicePixelRatio,
    wake_lock_supported: typeof navigator !== "undefined" && "wakeLock" in navigator,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    ms_since_last_interaction: lastInteractionAt ? Date.now() - lastInteractionAt : undefined,
    last_interaction_at: lastInteractionAt ? new Date(lastInteractionAt).toISOString() : null,
    wake_lock_active: wakeLockActive,
    wake_lock_last_change_at: wakeLockLastChangeAt,
    wake_lock_last_change: wakeLockLastChange,
    doc_state: lifecycle,
    last_visibility_transition: visibilityLog[0]?.kind ?? null,
    last_visibility_transition_at: visibilityLog[0]?.at ?? null,
    battery_charging: cachedBatteryCharging,
    prefers_reduced_motion: reducedMotion,
  };
};

export interface OfflineReasonEntry {
  at: string;
  userId: string | null;
  reason: string; // e.g. "screen-locked-90s", "manual-toggle"
  detail?: string;
  hiddenForMs?: number;
  snapshot?: OfflineSnapshot;
  /**
   * Stable client-generated correlation id. Same value is sent to the
   * backend row inside `snapshot.client_event_id`, so exported JSON/CSV
   * files can be matched 1:1 against backend audit rows even when the
   * backend `id` (audit_id) hasn't yet been observed locally.
   */
  eventId?: string;
  /**
   * Backend audit row id (`vet_offline_events.id`), populated once the
   * background queue successfully inserts the row. Lets exports link to a
   * specific server-side log entry without ambiguity.
   */
  auditId?: string;
}

const OFFLINE_LOG_KEY = "gpv:offline-log";
const OFFLINE_LOG_LIMIT = 20;
const OFFLINE_DETAIL_MAX = 240;
const OFFLINE_REASON_MAX = 80;

/**
 * Build a slim snapshot for localStorage. Drops large/PII fields (UA, URL,
 * sub-event ISO timestamps, viewport/screen dims) and keeps just the signals
 * needed to understand *why* the vet went offline. The full snapshot is still
 * sent to the backend audit table on insert.
 */
const slimSnapshot = (s: OfflineSnapshot | undefined): OfflineSnapshot | undefined => {
  if (!s) return undefined;
  const out: OfflineSnapshot = {};
  if (s.visibility_state) out.visibility_state = s.visibility_state;
  if (typeof s.has_focus === "boolean") out.has_focus = s.has_focus;
  if (typeof s.online === "boolean") out.online = s.online;
  if (typeof s.wake_lock_active === "boolean") out.wake_lock_active = s.wake_lock_active;
  if (s.wake_lock_last_change) out.wake_lock_last_change = s.wake_lock_last_change;
  if (typeof s.ms_since_last_interaction === "number") {
    // Round to nearest second - sub-second precision isn't useful and shrinks
    // the JSON.
    out.ms_since_last_interaction =
      Math.round(s.ms_since_last_interaction / 1000) * 1000;
  }
  if (s.last_visibility_transition) out.last_visibility_transition = s.last_visibility_transition;
  if (s.battery_charging !== undefined && s.battery_charging !== null) {
    out.battery_charging = s.battery_charging;
  }
  // Always preserve the client correlation id so slim/persisted entries can
  // still be matched against backend audit rows after a refresh.
  if (s.client_event_id) out.client_event_id = s.client_event_id;
  // Deliberately omitted from localStorage: url, user_agent, viewport,
  // screen, device_pixel_ratio, page_lifecycle_state, wake_lock_supported,
  // last_interaction_at, last_visibility_transition_at, doc_state,
  // prefers_reduced_motion, wake_lock_last_change_at. All of these are still
  // captured in the in-memory + backend snapshot.
  return out;
};

const slimEntry = (entry: OfflineReasonEntry): OfflineReasonEntry => ({
  at: entry.at,
  userId: entry.userId,
  reason: entry.reason.slice(0, OFFLINE_REASON_MAX),
  ...(entry.detail ? { detail: entry.detail.slice(0, OFFLINE_DETAIL_MAX) } : {}),
  ...(typeof entry.hiddenForMs === "number"
    ? { hiddenForMs: Math.round(entry.hiddenForMs / 1000) * 1000 }
    : {}),
  ...(entry.snapshot ? { snapshot: slimSnapshot(entry.snapshot) } : {}),
  // Correlation IDs are tiny and critical for matching exports against
  // backend audit rows, so they survive the slim pass.
  ...(entry.eventId ? { eventId: entry.eventId } : {}),
  ...(entry.auditId ? { auditId: entry.auditId } : {}),
});

const loadPersistedOfflineLog = (): OfflineReasonEntry[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(OFFLINE_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, OFFLINE_LOG_LIMIT) : [];
  } catch {
    return [];
  }
};

let offlineLog: OfflineReasonEntry[] = loadPersistedOfflineLog();

const persistOfflineLog = () => {
  if (typeof window === "undefined") return;
  try {
    // Persist slimmed entries only. Keeps the payload bounded
    // (~20 entries × ~300 bytes ≈ ≤6 KB worst case) so localStorage can't
    // grow unbounded across long-running sessions.
    const slim = offlineLog.map(slimEntry);
    window.localStorage.setItem(OFFLINE_LOG_KEY, JSON.stringify(slim));
  } catch {
    /* ignore quota errors */
  }
};

export const logOfflineReason = (
  entry: Omit<OfflineReasonEntry, "at"> & { practiceId?: string | null },
) => {
  // Generate a stable client-side correlation id up-front so the local log
  // entry, the queued backend insert, and any export all share the same id.
  const eventId =
    entry.eventId ??
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `oe_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
  const snapshotWithId: OfflineSnapshot = {
    ...(entry.snapshot ?? {}),
    client_event_id: eventId,
  } as OfflineSnapshot;
  const rec: OfflineReasonEntry = {
    at: new Date().toISOString(),
    ...entry,
    eventId,
    snapshot: snapshotWithId,
  };
  // In-memory log keeps the FULL snapshot for the current session so the
  // debug panel can show rich detail without a backend round-trip;
  // persistOfflineLog slims it before writing to localStorage.
  offlineLog = [rec, ...offlineLog].slice(0, OFFLINE_LOG_LIMIT);
  persistOfflineLog();
  // eslint-disable-next-line no-console
  console.warn("[vet:offline]", rec);
  if (entry.userId) {
    void persistOfflineEventToBackend(rec, entry.practiceId ?? null);
  }
};

const persistOfflineEventToBackend = async (
  rec: OfflineReasonEntry,
  practiceId: string | null,
) => {
  // Route through the queue: handles offline browser, transient 5xx, dropped
  // sockets after sleep/wake, and persists across refresh until delivered.
  const { enqueueOfflineEvent } = await import("@/lib/offlineEventQueue");
  enqueueOfflineEvent({
    user_id: rec.userId as string,
    practice_id: practiceId,
    reason: rec.reason,
    detail: rec.detail ?? null,
    hidden_for_ms: rec.hiddenForMs ?? null,
    snapshot: rec.snapshot ?? null,
    client_at: rec.at,
    client_event_id: rec.eventId,
  });
};

export const getOfflineLog = (): ReadonlyArray<OfflineReasonEntry> => offlineLog;
export const getLastOfflineReason = (): OfflineReasonEntry | null => offlineLog[0] ?? null;
export const clearOfflineLog = () => { offlineLog = []; persistOfflineLog(); };

/**
 * Called by the offline-event queue once a row is successfully inserted on
 * the backend. Backfills the audit_id onto the matching local entry (matched
 * by client-generated event id) so subsequent exports can include both ids.
 */
export const attachOfflineAuditId = (eventId: string, auditId: string) => {
  let mutated = false;
  offlineLog = offlineLog.map((e) => {
    if (e.eventId === eventId && !e.auditId) {
      mutated = true;
      return { ...e, auditId };
    }
    return e;
  });
  if (mutated) persistOfflineLog();
};


// ---------------------------------------------------------------------------
// Auto-resume lifecycle events - lightweight telemetry so we can monitor
// adoption (enable/disable rate), success (fires), and friction (cancels).
// Persisted to the existing vet_offline_events table with an "auto-resume:*"
// reason prefix so we don't need a new schema. Also mirrored to console for
// session-replay capture and surfaced in the Vet Auth Debug panel.
// ---------------------------------------------------------------------------

export type AutoResumeEventKind =
  | "enabled"
  | "disabled"
  | "fired"
  | "cancelled-manual"
  | "cancelled-auto"
  | "failed";

export interface AutoResumeEvent {
  at: string;
  kind: AutoResumeEventKind;
  userId: string | null;
  practiceId?: string | null;
  detail?: string;
}

const AUTO_RESUME_LOG_LIMIT = 30;
let autoResumeLog: AutoResumeEvent[] = [];

export const logAutoResumeEvent = (entry: Omit<AutoResumeEvent, "at">) => {
  const rec: AutoResumeEvent = { at: new Date().toISOString(), ...entry };
  autoResumeLog = [rec, ...autoResumeLog].slice(0, AUTO_RESUME_LOG_LIMIT);
  // eslint-disable-next-line no-console
  console.info("[vet:auto-resume]", rec);
  if (entry.userId) {
    void persistAutoResumeEventToBackend(rec);
  }
};

const persistAutoResumeEventToBackend = async (rec: AutoResumeEvent) => {
  const { enqueueOfflineEvent } = await import("@/lib/offlineEventQueue");
  enqueueOfflineEvent({
    user_id: rec.userId as string,
    practice_id: rec.practiceId ?? null,
    reason: `auto-resume:${rec.kind}`,
    detail: rec.detail ?? null,
    client_at: rec.at,
  });
};

export const getAutoResumeLog = (): ReadonlyArray<AutoResumeEvent> => autoResumeLog;
export const clearAutoResumeLog = () => { autoResumeLog = []; };

// ---------------------------------------------------------------------------
// Visibility / focus / online transitions - feeds the offline-event timeline
// ---------------------------------------------------------------------------

export type VisibilityTransitionKind =
  | "visible"
  | "hidden"
  | "focus"
  | "blur"
  | "online"
  | "offline"
  | "pageshow"
  | "pagehide"
  | "freeze"
  | "resume";

export interface VisibilityTransition {
  at: string;
  kind: VisibilityTransitionKind;
}

const VISIBILITY_LOG_LIMIT = 60;
let visibilityLog: VisibilityTransition[] = [];

// ---- Backend persistence (admin-only audit) -----------------------------
// Buffer transitions client-side and flush in batches every ~30s (and on
// pagehide/freeze) to public.vet_visibility_transitions. Vets insert their own
// rows via the self-insert RLS policy; admins read via SECURITY DEFINER RPC.
type PendingTransition = { kind: VisibilityTransitionKind; client_at: string };
let pendingTransitions: PendingTransition[] = [];
let visibilityFlushTimer: ReturnType<typeof setInterval> | null = null;
let visibilityFlushCtx: { userId: string | null; practiceId: string | null } = {
  userId: null,
  practiceId: null,
};
const VISIBILITY_FLUSH_INTERVAL_MS = 30_000;
const VISIBILITY_FLUSH_BATCH_MAX = 200;
const VISIBILITY_BUFFER_MAX = 500;

export const setVisibilityFlushContext = (userId: string | null, practiceId: string | null) => {
  visibilityFlushCtx = { userId, practiceId };
};

const flushVisibilityTransitions = async () => {
  if (pendingTransitions.length === 0) return;
  const { userId, practiceId } = visibilityFlushCtx;
  if (!userId) return; // not signed in / context not set yet - keep buffering
  const batch = pendingTransitions.splice(0, VISIBILITY_FLUSH_BATCH_MAX);
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const rows = batch.map((t) => ({
      user_id: userId,
      practice_id: practiceId,
      kind: t.kind,
      client_at: t.client_at,
    }));
    const { error } = await supabase.from("vet_visibility_transitions").insert(rows);
    if (error) {
      pendingTransitions = [...batch, ...pendingTransitions].slice(0, VISIBILITY_BUFFER_MAX);
      // eslint-disable-next-line no-console
      console.warn("[vet:visibility-flush] insert failed", error.message);
    }
  } catch (e) {
    pendingTransitions = [...batch, ...pendingTransitions].slice(0, VISIBILITY_BUFFER_MAX);
    // eslint-disable-next-line no-console
    console.warn("[vet:visibility-flush] unexpected error", e);
  }
};

const recordVisibilityTransition = (kind: VisibilityTransitionKind) => {
  const at = new Date().toISOString();
  const rec: VisibilityTransition = { at, kind };
  visibilityLog = [rec, ...visibilityLog].slice(0, VISIBILITY_LOG_LIMIT);
  pendingTransitions.push({ kind, client_at: at });
  if (pendingTransitions.length > VISIBILITY_BUFFER_MAX) {
    pendingTransitions = pendingTransitions.slice(-VISIBILITY_BUFFER_MAX);
  }
};

export const getVisibilityLog = (): ReadonlyArray<VisibilityTransition> => visibilityLog;

/** Idempotently install global listeners that capture visibility/focus/online transitions. */
let visibilityListenersInstalled = false;
export const installVisibilityTracker = () => {
  if (visibilityListenersInstalled || typeof window === "undefined") return;
  visibilityListenersInstalled = true;
  recordVisibilityTransition(document.visibilityState === "visible" ? "visible" : "hidden");
  document.addEventListener("visibilitychange", () => {
    recordVisibilityTransition(document.visibilityState === "visible" ? "visible" : "hidden");
  });
  window.addEventListener("focus", () => recordVisibilityTransition("focus"));
  window.addEventListener("blur", () => recordVisibilityTransition("blur"));
  window.addEventListener("online", () => recordVisibilityTransition("online"));
  window.addEventListener("offline", () => recordVisibilityTransition("offline"));
  window.addEventListener("pageshow", () => recordVisibilityTransition("pageshow"));
  window.addEventListener("pagehide", () => {
    recordVisibilityTransition("pagehide");
    void flushVisibilityTransitions();
  });
  document.addEventListener("freeze", () => {
    recordVisibilityTransition("freeze");
    void flushVisibilityTransitions();
  });
  document.addEventListener("resume", () => recordVisibilityTransition("resume"));

  if (visibilityFlushTimer === null) {
    visibilityFlushTimer = setInterval(() => {
      void flushVisibilityTransitions();
    }, VISIBILITY_FLUSH_INTERVAL_MS);
  }
};

export const recordAuthEvent = (event: string, session: { user?: { id?: string | null } | null; expires_at?: number | null } | null) => {
  const rec: AuthEventRecord = {
    event,
    at: new Date().toISOString(),
    hasSession: !!session,
    userId: session?.user?.id ?? null,
    expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
  };
  authState.lastEvent = rec;
  authState.history = [rec, ...authState.history].slice(0, HISTORY_LIMIT);
  if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN" || event === "INITIAL_SESSION") {
    authState.lastRefreshAt = rec.at;
  }
  if (event === "SIGNED_OUT") {
    authState.lastSignOutAt = rec.at;
    const lastLogged = authState.signOutLog[0];
    const recentlyAttributed =
      lastLogged && Date.now() - new Date(lastLogged.at).getTime() < 2000;
    if (!recentlyAttributed) {
      logSignOut({
        userId: rec.userId ?? null,
        trigger: "supabase:SIGNED_OUT",
        reason: "Auth state change with no session (token revoked, refresh failed, or external signOut)",
      });
    }
  }
};

export const recordSignOutReason = (reason: string) => {
  authState.lastSignOutReason = reason;
  authState.lastSignOutAt = new Date().toISOString();
};

export const recordGateRedirect = (gate: string, to: string, reason?: string) => {
  authState.lastGateRedirect = {
    gate,
    to,
    reason,
    at: new Date().toISOString(),
  };
};

export const getAuthDiagnostics = (): Readonly<AuthState> => authState;

// ---------------------------------------------------------------------------

export const buildDiagnosticsBlob = (input: {
  scope: string;
  userId?: string | null;
  role?: string | null;
  extra?: Record<string, unknown>;
}) => {
  return {
    scope: input.scope,
    user_id: input.userId ?? null,
    role: input.role ?? null,
    url: typeof window !== "undefined" ? window.location.href : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    captured_at: new Date().toISOString(),
    last_query_error: lastError,
    auth: {
      last_event: authState.lastEvent,
      last_refresh_at: authState.lastRefreshAt,
      last_sign_out_at: authState.lastSignOutAt,
      last_sign_out_reason: authState.lastSignOutReason,
      last_gate_redirect: authState.lastGateRedirect,
      sign_out_log: authState.signOutLog,
    },
    vet_availability: {
      last_offline_reason: offlineLog[0] ?? null,
      offline_log: offlineLog,
    },
    ...(input.extra ? { extra: input.extra } : {}),
  };
};
